import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { adoptDevice, claimDevice, ensureDevice, takeOverDevice } from '../data/repository';
import type { DeviceClaim } from '../data/repository';
import { report } from '../sentry';
import { Hint, PrimaryButton, Screen, SecondaryButton, Title } from './kit';

/** Сверка того, чьи данные лежат на устройстве, — ПЕРЕД профилями и обменом.
 *
 *  Дефект, который этот заслон закрывает, живёт в приложении с самого
 *  появления учётных записей и от работы до входа не зависит. Устройство не
 *  помнило, чьи на нём занятия, и сравнивать было не с чем. Дорога такая:
 *  родитель А вошёл на планшете; сессия умерла (`SIGNED_OUT` приходит и при
 *  неудачном обновлении токена, то есть просто от плохой связи); появился
 *  экран «войдите заново»; вошёл родитель Б — и увидел на одном экране детей
 *  обеих семей. Стирание при выходе тут не помогает: выхода не было.
 *
 *  Почему заслон стоит ДО обмена, а не рядом с ним. Отправка унесла бы
 *  занятия прежней семьи под записью новой — сервер их отвергнет (проверка
 *  владения в триггере), но взрослый увидит отказ вместо объяснения. Докачка
 *  подмешала бы детей новой семьи к детям прежней. Оба следствия надо не
 *  обрабатывать, а не допускать.
 *
 *  Стирание предлагается, но не делается само: оно необратимо, а данные
 *  принадлежат не тому, кто сейчас у экрана. Второй выход — выйти и позвать
 *  того, чьи это занятия. */
export function DeviceOwnerGate({
  parentId, email, onSignOut, children,
}: {
  parentId: string;
  /** Почта вошедшего. Нужна ровно для одного: назвать её вслух, когда
   *  устройство закрепляется за этой записью. */
  email: string;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const [claim, setClaim] = useState<DeviceClaim | null>(null);
  const [told, setTold] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setClaim(null);
    setTold(false);
    setFailed(false);
    /** Строка устройства заводится ЗДЕСЬ, до сверки.
     *
     *  Найдено живым прогоном на деве, и тестами не ловилось: они либо
     *  подменяют репозиторий, либо заводят строку заранее. На совершенно
     *  новом устройстве её нет — её создаёт шлюз профилей, а он стоит НИЖЕ
     *  этого заслона. Сверка отвечала «усыновлять нечего», закрепление
     *  падало на отсутствующей строке, и первый в жизни вход упирался в
     *  экран отказа. То есть блокирующая поломка на самой частой дороге —
     *  установил и открыл.
     *
     *  `ensureDevice` идемпотентен: ниже его позовёт шлюз профилей, и это
     *  ничего не изменит. */
    void ensureDevice(() => crypto.randomUUID())
      .then(() => claimDevice(parentId))
      .then(async (result) => {
        /** Закреплять нечего — устройство пустое. Отметку ставим молча:
         *  спрашивать не о чем, а оставить устройство без владельца нельзя,
         *  иначе следующий вошедший снова считался бы усыновителем и заслон
         *  не защищал бы ни от чего.
         *
         *  Обратите внимание, чего здесь НЕТ: проверки «а все ли профили уже
         *  известны серверу». Она тут была и оказалась дырой — `serverKnown`
         *  говорит «строка есть на КАКОМ-ТО сервере», а не «на сервере ЭТОГО
         *  родителя», и давняя установка закреплялась за чужой записью
         *  молча. Спрашиваем при любом числе детей больше нуля. */
        if (result.kind === 'adopted' && result.profiles === 0) await adoptDevice(parentId);
        return result;
      })
      .then((result) => {
        if (alive) setClaim(result);
      })
      .catch((cause: unknown) => {
        /** Без ответа заслон показывал пустой экран навсегда. Пустой экран —
         *  худший из отказов: он не отличим от медленной загрузки, и взрослый
         *  ждёт, а не чинит. */
        report(cause, 'DeviceOwnerGate');
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [parentId]);

  if (failed) {
    return (
      <Screen>
        <Title>Не получилось прочитать устройство</Title>
        <Hint>
          Мы уже знаем об этой ошибке. Занятия на устройстве целы — перезагрузите страницу.
        </Hint>
        <PrimaryButton onClick={() => window.location.assign('/')}>Обновить</PrimaryButton>
        <SecondaryButton bottom onClick={onSignOut}>
          Выйти
        </SecondaryButton>
      </Screen>
    );
  }

  /** Пока ответа нет — пусто. Показать детей до сверки значит на мгновение
   *  показать чужих, а мгновения хватает: экран выбора ребёнка — это первое,
   *  куда смотрит взрослый. */
  if (claim === null) return null;

  if (claim.kind === 'foreign') {
    return (
      <Screen>
        <Title>На этом устройстве занятия другой семьи</Title>
        <Hint>
          Здесь остались профили ({claim.profiles}) и занятия другой учётной записи. Показать
          их вам нельзя, а смешивать с вашими — тем более.
        </Hint>
        <Hint>
          Если планшет ваш и прежняя запись больше не нужна, занятия можно стереть. Если это
          чужие занятия — выйдите и позовите того, чьи они: под своей записью он их увидит,
          а стёртое не вернётся.
        </Hint>
        <PrimaryButton
          disabled={busy}
          onClick={() => {
            setBusy(true);
            takeOverDevice(parentId).then(
              () => {
                setBusy(false);
                /** Стирание было решением взрослого, и рассказывать ему о
                 *  переходе занятий, которых больше нет, незачем. */
                setTold(true);
                setClaim({ kind: 'adopted', profiles: 0, fresh: 0 });
              },
              (cause: unknown) => {
                report(cause, 'takeOverDevice');
                setBusy(false);
                setFailed(true);
              },
            );
          }}
        >
          Стереть и продолжить
        </PrimaryButton>
        <SecondaryButton bottom onClick={onSignOut}>
          Выйти
        </SecondaryButton>
      </Screen>
    );
  }

  /** Громкое закрепление устройства.
   *
   *  Дорога, ради которой это написано: взрослый занимался без записи (или
   *  пользовался приложением до появления этого поля), потом вошёл — и
   *  занятия ребёнка стали принадлежать конкретной почте. Если почта не та
   *  (прежняя своя, супруга, чужая на общем планшете), единственный момент,
   *  когда это можно заметить, — вот этот. Дальше всё выглядит здоровым:
   *  экран работает, занятия идут, просто они не там, где их будут искать. */
  if (claim.kind === 'adopted' && claim.profiles > 0 && !told) {
    return (
      <Screen>
        <Title>Это устройство закрепляется за вашей записью</Title>
        <Hint>
          {claim.fresh > 0
            ? `Занятия детей на этом устройстве (${claim.profiles}) теперь сохраняются в записи ${email} и появятся на других устройствах после входа в неё.`
            : `Дети на этом устройстве (${claim.profiles}) теперь закреплены за записью ${email}. Занятия останутся на месте — меняется только то, что устройство больше не пустит сюда другую запись.`}
        </Hint>
        <Hint>
          Если это не та почта, выйдите и войдите в нужную: пока вы не нажали «Хорошо»,
          устройство ни за кем не закреплено.
        </Hint>
        <PrimaryButton
          disabled={busy}
          onClick={() => {
            setBusy(true);
            adoptDevice(parentId).then(
              () => {
                setBusy(false);
                setTold(true);
              },
              (cause: unknown) => {
                report(cause, 'adoptDevice');
                setBusy(false);
                setFailed(true);
              },
            );
          }}
        >
          Хорошо
        </PrimaryButton>
        <SecondaryButton bottom onClick={onSignOut}>
          Это не та почта, выйти
        </SecondaryButton>
      </Screen>
    );
  }

  return <>{children}</>;
}
